import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Character } from '@/schemas/character';
import { saveCharacter } from '@/services/characterStorage';
import { writeUndoSnapshot } from '@/services/undoSnapshot';
import type { dwrClassesById } from '@/data/classes';
import { tryApplyLevelUp } from './applyLevelUpHelpers';
import type { LevelUpDraft } from './types';

/**
 * **FI-46 PR-B**: hook che incapsula state + handlers di apply/cancel del
 * `LevelUpFlowPage`. Estratto da `LevelUpFlowInner` per rispettare
 * `max-lines-per-function: 100`.
 *
 * Distingue automaticamente modalità "create" (snapshot/messaggio standard)
 * vs "edit" (snapshot del PG **originale** pre-rewind → l'Annulla restituisce
 * tutta la cronologia precedente intatta; dialog di truncate prima di applicare).
 */

type ClassDef = NonNullable<(typeof dwrClassesById)[keyof typeof dwrClassesById]>;

export interface LevelUpFlowHandlersDeps {
  /** PG mostrato al flow (post-rewind in edit mode). */
  character: Character;
  /** PG **originale** (pre-rewind). In create mode === character. */
  originalCharacter: Character;
  classDef: ClassDef | undefined;
  draft: LevelUpDraft;
  isEditMode: boolean;
  editLevel: number | undefined;
  nextLevel: number;
  truncatedLevels: number;
}

export function useLevelUpFlowHandlers(deps: LevelUpFlowHandlersDeps) {
  const navigate = useNavigate();
  const [applyError, setApplyError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [truncateDialogOpen, setTruncateDialogOpen] = useState(false);

  function doApply(): void {
    const result = tryApplyLevelUp(deps.character, deps.classDef, deps.draft);
    if (!result.ok) {
      setApplyError(result.error);
      return;
    }
    // Snapshot pre-apply per il toast «Annulla» (mockup styles.css 2063-2098).
    // In edit mode lo snapshot è il PG **originale** (pre-rewind), così
    // l'Annulla restituisce tutta la cronologia precedente intatta.
    const message = deps.isEditMode
      ? `Modifiche al livello ${deps.editLevel} applicate.`
      : `Sei salito al livello ${deps.nextLevel}.`;
    writeUndoSnapshot({ character: deps.originalCharacter, message });
    saveCharacter(result.updated);
    void navigate('/character');
  }

  function handleApply(): void {
    // FI-46 PR-B: in edit mode con livelli successivi da troncare, mostra
    // ConfirmDialog di avviso prima di applicare.
    if (deps.isEditMode && deps.truncatedLevels > 0) {
      setTruncateDialogOpen(true);
      return;
    }
    doApply();
  }

  // FI-47: dialog DWR custom invece di confirm() JS native.
  // FI-46 PR-B: in edit mode il draft è pre-popolato → mostra sempre
  // il dialog per safety.
  function requestCancel(): void {
    const hasSelections =
      deps.draft.statIncreased !== undefined ||
      deps.draft.moveAdded !== undefined ||
      deps.draft.pastTen !== undefined ||
      Object.keys(deps.draft.spellSelections).length > 0;
    if (deps.isEditMode || hasSelections) {
      setCancelDialogOpen(true);
      return;
    }
    void navigate('/character');
  }

  function confirmCancel(): void {
    setCancelDialogOpen(false);
    void navigate('/character');
  }

  function confirmTruncate(): void {
    setTruncateDialogOpen(false);
    doApply();
  }

  return {
    applyError,
    cancelDialogOpen,
    setCancelDialogOpen,
    truncateDialogOpen,
    setTruncateDialogOpen,
    handleApply,
    requestCancel,
    confirmCancel,
    confirmTruncate,
  };
}
